import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

// Admin Dashboard Components
const AdminOverview = lazy(() => import('../components/admin/AdminOverview'));
const EmployeeList = lazy(() => import('../components/admin/EmployeeList'));
const CreateEmployee = lazy(() => import('../components/admin/CreateEmployee'));
const LeaveRequestsList = lazy(() => import('../components/admin/LeaveRequestsList'));
const PermissionsList = lazy(() => import('../components/admin/PermissionsList'));
const ClaimsList = lazy(() => import('../components/admin/ClaimsList'));
const AttendanceReport = lazy(() => import('../components/admin/AttendanceReport'));
const RefreshmentsReport = lazy(() => import('../components/admin/RefreshmentsReport'));
const Payslip = lazy(() => import('../components/admin/Payslip'));
const TaskReports = lazy(() => import('../components/admin/TaskReports'));
const Reports = lazy(() => import('../components/admin/Reports'));
const Settings = lazy(() => import('../components/admin/Settings'));
const CreateTask = lazy(() => import('../components/admin/CreateTask'));
const AssignTask = lazy(() => import('../components/admin/AssignTask'));

const AdminDashboard = () => {
  const location = useLocation();
  const { user, api } = useAuth();
  const [sidebarShow, setSidebarShow] = useState(false);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0
  });
  const [loading, setLoading] = useState(true);

  // Determine active menu item based on current path
  const getActiveItem = () => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/') return 'dashboard';
    if (path.includes('/employees')) return 'employees';
    if (path.includes('/create-employee')) return 'create-employee';
    if (path.includes('/leave-requests')) return 'leave-requests';
    if (path.includes('/permissions')) return 'permissions';
    if (path.includes('/claims')) return 'claims';
    if (path.includes('/attendance')) return 'attendance';
    if (path.includes('/refreshments')) return 'refreshments';
    if (path.includes('/payslip')) return 'payslip';
    if (path.includes('/task-reports')) return 'task-reports';
    if (path.includes('/create-task')) return 'create-task';
    if (path.includes('/assign-task')) return 'assign-task';
    if (path.includes('/reports')) return 'reports';
    if (path.includes('/settings')) return 'settings';
    return 'dashboard';
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch employees count
      const employeesResponse = await api.get('/employees?limit=1');
      const totalEmployees = employeesResponse.data.total || 0;
      
      // Fetch leave requests stats
      const statsResponse = await api.get('/leave-requests/stats/summary');
      
      setStats({
        totalEmployees,
        pendingRequests: statsResponse.data.pendingRequests || 0,
        approvedRequests: statsResponse.data.approvedRequests || 0,
        rejectedRequests: statsResponse.data.rejectedRequests || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarShow(!sidebarShow);
  };

  if (loading && location.pathname === '/admin') {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  return (
    <div className="dashboard-container">
      <Sidebar 
        activeItem={getActiveItem()} 
        userRole="admin" 
        show={sidebarShow}
        onClose={() => setSidebarShow(false)}
      />
      
      {/* Mobile sidebar toggle */}
      <Button
        variant="primary"
        className="d-lg-none position-fixed mobile-menu-toggle"
        style={{ top: '15px', left: '15px', zIndex: 1001 }}
        onClick={toggleSidebar}
      >
        <i className="fas fa-bars"></i>
      </Button>

      <div className="main-content">
        <Suspense fallback={<LoadingSpinner message="Loading page..." />}>
          <Routes>
            <Route 
              path="/" 
              element={<AdminOverview stats={stats} refreshStats={fetchStats} />} 
            />
            <Route 
              path="/employees" 
              element={<EmployeeList />} 
            />
            <Route 
              path="/create-employee" 
              element={<CreateEmployee onEmployeeCreated={fetchStats} />} 
            />
            <Route 
              path="/leave-requests" 
              element={<LeaveRequestsList refreshStats={fetchStats} />} 
            />
            <Route 
              path="/permissions" 
              element={<PermissionsList />} 
            />
            <Route 
              path="/claims" 
              element={<ClaimsList />} 
            />
            <Route 
              path="/attendance" 
              element={<AttendanceReport />} 
            />
            <Route 
              path="/refreshments" 
              element={<RefreshmentsReport />} 
            />
            <Route 
              path="/payslip" 
              element={<Payslip />} 
            />
            <Route 
              path="/task-reports" 
              element={<TaskReports />} 
            />
            <Route 
              path="/create-task" 
              element={<CreateTask />} 
            />
            <Route 
              path="/assign-task" 
              element={<AssignTask />} 
            />
            <Route 
              path="/reports" 
              element={<Reports />} 
            />
            <Route 
              path="/settings" 
              element={<Settings />} 
            />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
};

export default AdminDashboard;